import { supabase } from '../../shared/supabase/supabaseClient';

/** TTL corto: el carrito no necesita exactitud milimétrica; post-venta refresca en ≤TTL. */
const USAGE_COUNTS_TTL_MS = 30_000;

type CacheEntry = {
  expiresAt: number;
  map: Map<string, number>;
  key: string;
};

let cache: CacheEntry | null = null;

function cacheKey(promotionIds: string[] | null): string {
  if (!promotionIds || promotionIds.length === 0) return '*';
  return [...promotionIds].sort().join(',');
}

function parseCounts(raw: unknown): Map<string, number> {
  const map = new Map<string, number>();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return map;
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    const n = typeof value === 'number' ? value : Number(value);
    if (id && Number.isFinite(n) && n > 0) map.set(id, Math.floor(n));
  }
  return map;
}

/**
 * Carga usos por promo vía `pos.get_promotion_usage_counts`.
 * Si `promotionIds` está vacío, retorna Map vacío sin RPC.
 */
export async function fetchPromotionUsageCounts(
  promotionIds: string[],
  options?: { organizationId?: string | null; force?: boolean },
): Promise<Map<string, number>> {
  const ids = promotionIds.map((id) => id.trim()).filter(Boolean);
  if (ids.length === 0) return new Map();

  const key = cacheKey(ids);
  const now = Date.now();
  if (!options?.force && cache && cache.key === key && cache.expiresAt > now) {
    return cache.map;
  }

  const { data, error } = await supabase.schema('pos').rpc('get_promotion_usage_counts', {
    p_organization_id: options?.organizationId ?? null,
    p_promotion_ids: ids,
  });

  if (error) {
    console.warn('[promotionUsageCounts] RPC failed', error.message);
    throw error;
  }

  const map = parseCounts(data);
  cache = { key, map, expiresAt: now + USAGE_COUNTS_TTL_MS };
  return map;
}

export function promotionIdsNeedingUsageCount(
  promotions: Array<{ id: string; conditions?: { usageLimit?: number | null } | null }>,
): string[] {
  return promotions
    .filter((p) => p.conditions?.usageLimit != null && Number(p.conditions.usageLimit) > 0)
    .map((p) => p.id);
}
